import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { TEST_PREFIX, PROD_PREFIX } from '../config/databasePaths';

// Test mode prefix for Firebase paths (now using v1 versioned paths)
export const TEST_MODE_PREFIX = TEST_PREFIX;  // 'test-mode/v1'
export const PROD_MODE_PREFIX = PROD_PREFIX;  // 'v1'

interface TestModeContextType {
  isTestMode: boolean;
  toggleTestMode: () => void;
  getPath: (path: string) => string;
}

const TestModeContext = createContext<TestModeContextType>({
  isTestMode: false,
  toggleTestMode: () => {},
  getPath: (path: string) => path,
});

export const useTestMode = () => useContext(TestModeContext);

export const TestModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin } = useAuth();
  const [isTestMode, setIsTestMode] = useState(false);

  // Load test mode state from localStorage (only for admins)
  useEffect(() => {
    if (isAdmin) {
      const saved = localStorage.getItem('pg_test_mode');
      if (saved === 'true') {
        setIsTestMode(true);
      }
    } else {
      // Non-admins should never be in test mode
      setIsTestMode(false);
    }
  }, [isAdmin]);

  // Toggle test mode
  const toggleTestMode = useCallback(() => {
    if (!isAdmin) return;

    setIsTestMode(prev => {
      const newValue = !prev;
      localStorage.setItem('pg_test_mode', String(newValue));
      // Force page reload to refresh all subscriptions
      window.location.reload();
      return newValue;
    });
  }, [isAdmin]);

  // Get path with versioned prefix (v1 or test-mode/v1)
  const getPath = useCallback((path: string) => {
    // Don't prefix paths that are already prefixed with v1 or test-mode
    if (path.startsWith('v1/') || path.startsWith('test-mode/') || path.startsWith('admins')) {
      return path;
    }

    // Use test-mode/v1 prefix for test mode, v1 prefix for production
    const prefix = (isTestMode && isAdmin) ? TEST_MODE_PREFIX : PROD_MODE_PREFIX;
    return `${prefix}/${path}`;
  }, [isTestMode, isAdmin]);

  return (
    <TestModeContext.Provider value={{ isTestMode, toggleTestMode, getPath }}>
      {children}
    </TestModeContext.Provider>
  );
};

export default TestModeContext;
